import { ErrorCode } from '@ps/web-core-enums';
import { DebugKey, canUseDOM, createDebug } from '@ps/web-core-utils';
import { mapKeys, mapValues, pickBy, snakeCase } from 'lodash';
import { defer, lastValueFrom } from 'rxjs';
import { filter, first, switchMap, tap, timeout } from 'rxjs/operators';
import URL from 'url-parse';
import type { Required } from 'utility-types';

import { Header } from '@swf/core/enums';
import { StatusCode } from '@swf/core/enums/statusCode';
import { performPow } from '@swf/core/utils/api';

import { RequestError } from '../errors/RequestError';
import type { RequestOptions, RequestReturn, ResponseMessage } from '../types/Request';

import { WMTDisconnectReason } from './config';
import { State, connect } from './connect';
import { handleError } from './handleError';
import { subscribe } from './subscribe';

export type { Message } from '@swf/core/types/Notification';

/* eslint-disable @typescript-eslint/no-explicit-any, no-console, @typescript-eslint/no-non-null-assertion */

const versionRegExp = /^\/api\/v(\d)/;
const defaultHttpMethod = 'GET';

const errorCase = (name: string) => snakeCase(name).toUpperCase();

let histogram: any = null;
let messageId = 0;

if (!canUseDOM) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const prometheus = require('prom-client');
  histogram = new prometheus.Histogram({
    name: 'vader_wmt_requests',
    help: 'Request duration in milliseconds',
    buckets: [100, 500, 1000, 3000, 5000],
    labelNames: ['service']
  });
}

const isGetLikeRequest = (
  method: RequestOptions['method'] = defaultHttpMethod
): method is 'GET' | 'HEAD' => ['GET', 'HEAD'].includes(method);

const success = (startTime: number, service: string, body: any, requestId?: string) => {
  const debug = createDebug(DebugKey.Request, requestId);
  const info = createDebug(DebugKey.RequestInfo, requestId);
  const duration = Date.now() - startTime;
  debug('success %s [%dms]', service, duration);
  info('success %s %o', service, body);
  if (histogram) {
    histogram.observe({ service }, duration);
  }
};

const httpRequest = <B = any>(
  url: string,
  init: Required<RequestInit, 'method' | 'headers'>,
  service: string,
  startTime: number,
  isEmbedded: boolean
): RequestReturn<B> => {
  const initHeaders = init.headers as Record<string, string>;
  const requestId = initHeaders[Header.RequestId];

  return fetch(url, init)
    .catch((err) => {
      throw new RequestError({
        service,
        method: init.method,
        body: { code: errorCase(err.name), text: err.message },
        requestId,
        cause: err
      });
    })
    .then(async (response: Response) => {
      const text = await response.text();
      let body;

      try {
        if (text) body = JSON.parse(text);
      } catch (err) {
        const { type, name, message } = err as RequestError & { type?: string };
        throw new RequestError({
          service,
          method: init.method,
          body: { code: errorCase(type || name), text: message },
          response,
          requestId,
          cause: err
        });
      }

      if (response.ok) {
        success(startTime, service, body, requestId);
        return { headers: Object.fromEntries(response.headers.entries()), body };
      } else if (body?.code === ErrorCode.POW_REQUIRED && !initHeaders[Header.PowResult]) {
        (init.headers as Record<string, string>)[Header.PowResult] = performPow(body.challenge);
        return httpRequest(url, init, service, startTime, isEmbedded);
      }

      throw new RequestError({ service, body, response, method: init.method, requestId });
    });
};

export const request = <B = any>({
  service,
  headers: requestHeaders,
  meta,
  body,
  method = defaultHttpMethod,
  connection = connect(),
  http,
  httpDomain,
  timeout: timeoutOverride,
  silent,
  signal,
  retry = { count: 0 }
}: RequestOptions): RequestReturn<B> => {
  const requestId = requestHeaders?.[Header.RequestId];
  const debug = createDebug(DebugKey.Request, requestId);

  const id = messageId++;
  body ? debug('start %s %o', service, body) : debug('start %s', service);
  const startTime = Date.now();
  const requestBody = JSON.stringify(body);
  const [, version = 0] = service.match(versionRegExp)?.map((item) => parseInt(item)) || [];
  let headers: Required<RequestOptions>['headers'] = {
    ...requestHeaders,
    accept: 'application/json'
  };

  if (http || version > 0) {
    headers[Header.Locale] = meta.locale;
    headers[Header.Currency] = meta.currency;
    if (!canUseDOM) {
      headers[Header.Brand] = meta.brand?.toString() ?? '';
      headers[Header.Site] = meta.site?.toString() ?? '';
      headers[Header.Embedded] = meta.embedded?.toString() ?? '';
      headers[Header.Device] = meta.device?.toString() ?? '';
      headers[Header.OS] = meta.os?.toString() ?? '';
      headers[Header.Country] = meta.country?.toString() ?? '';
      headers[Header.State] = meta.state?.toString() ?? '';
      headers = pickBy(headers, (_key, value) => !!value);
    }
  } else if (meta) {
    const { systemState, ...v0Headers } = meta;
    Object.assign(headers, v0Headers);
  }

  if (http) {
    const url = new URL(service, httpDomain);
    let httpBody: any;

    if (canUseDOM) {
      delete headers.referer;
    }

    if (isGetLikeRequest(method)) {
      const query = mapValues(body, (item) =>
        typeof item !== 'string' ? JSON.stringify(item) : item
      );
      url.set('query', query);
      httpBody = undefined;
    } else {
      headers['content-type'] = 'application/json';
      httpBody = requestBody;
    }

    if ((body instanceof URLSearchParams || body instanceof FormData) && method === 'POST') {
      delete headers['content-type'];
      httpBody = body;
    }

    const isEmbedded = !!meta.embedded;

    return lastValueFrom(
      defer(() =>
        httpRequest(
          url.toString(),
          { method, headers, body: httpBody, signal, credentials: 'include' },
          service,
          startTime,
          isEmbedded
        )
      ).pipe(
        handleError({ retry, method, service, requestId }),
        tap({
          error: (error: RequestError | Error) =>
            error instanceof RequestError ? error.log() : debug(`requestId ${requestId} ${error}`)
        })
      )
    );
  }

  headers['message-id'] = id.toString();
  headers['content-type'] = 'application/json';
  if (version === 0) {
    headers.type = service;
  } else {
    const [, , , ...rest] = service.split('/');
    headers.destination = `/${rest.join('/')}`;
    headers['x-sw-version'] = version.toString();
    headers.method = method;
  }

  return lastValueFrom(
    connection.pipe(
      tap(({ client, state, config, disconnectReason }) => {
        switch (true) {
          case disconnectReason === WMTDisconnectReason.InvalidToken:
            delete headers.authorization;
            break;
          case state === State.CLOSED:
            throw new RequestError({
              service,
              method,
              body: {
                code: 'CONNECTION_CLOSED',
                text: 'The connection was closed before a response was received.'
              },
              requestId
            });
          case state === State.CONNECTED:
            client!.send(
              headers.destination || config!.requestDestination,
              mapKeys(headers, (value, key) => key.toLowerCase()),
              requestBody
            );
            break;
        }
      }),
      filter(({ state }) => state === State.CONNECTED),
      switchMap(({ client, config }) => {
        const { requestDestination, requestTimeout } = config!;
        const actualTimeout = timeoutOverride || requestTimeout;
        const responseChannel = version > 0 ? '/user/queue/response' : config!.responseChannel;

        return subscribe<ResponseMessage<B>>(responseChannel, connection).pipe(
          first((message) => {
            const {
              headers: { statusCode, replyId },
              body
            } = message;
            if (replyId !== id) return false;

            if (version > 0) {
              const status = statusCode as number;
              // eslint-disable-next-line @typescript-eslint/no-var-requires
              const Blob = canUseDOM ? window.Blob : require('buffer').Blob;
              if (status >= 400) {
                throw new RequestError({
                  service,
                  body,
                  response: new Response(new Blob(), { status }),
                  method,
                  requestId
                });
              }
              // TODO v1 POW handling
              success(startTime, service, message.body, requestId);
              return true;
            }

            switch (statusCode) {
              case StatusCode.POW_REQUIRED:
                headers[Header.PowResult] = performPow(message.body as unknown as string);
                client!.send(requestDestination, headers, requestBody);
                return false;
              case StatusCode.OK:
                success(startTime, service, message.body, requestId);
                return true;
              default:
                throw new RequestError({ ...message, service, method, requestId });
            }
          }),
          timeout(actualTimeout)
        );
      }),
      handleError({ retry, method, service, requestId }),
      tap({
        error: (error: RequestError | Error) => {
          if (!silent) error instanceof RequestError ? error.log() : console.error(error);
        }
      }),
      first()
    )
  );
};
